import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
    Box,
    Button,
    ButtonGroup,
    MenuItem,
    Typography
} from '@mui/material';
import { usePostHog } from '@posthog/react';
import { TrackedTextField } from 'src/theme/StyledFields';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { capture } from '../../analytics/posthog';
import ApiClient from '../../config/ApiClient';
import { useAppContext } from '../../context/AppContext';
import { useLessonPlanRecording } from '../../context/LessonPlanRecordingContext';
import { LessonPlanRole, LessonPlanSessionModel, SessionSlideModel } from '../../object-actions/types/types';
import LessonPlanSlide from './LessonPlanSlide';
import StopIcon from "@mui/icons-material/Stop";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";


interface LessonPlanSlideDeckProps {
    session: LessonPlanSessionModel;
    lessonPlanId?: number | null;
    isExpanded?: boolean; // Only track progress when expanded
    myRoles?: LessonPlanRole[];
    isPublished?: boolean;
    /** When provided, called after a successful update_slide_progress to update local state so Mark Complete becomes visible without refetch. */
    updateSlide?: (slideId: number, updates: Partial<SessionSlideModel>) => void;
    updateSession?: (sessionId: number, updates: Partial<LessonPlanSessionModel>) => void;
}

const LessonPlanSlideDeck: React.FC<LessonPlanSlideDeckProps> = ({ session, lessonPlanId, isExpanded = false, myRoles, isPublished = false, updateSlide: updateSlideProp, updateSession: updateSessionProp }) => {
    const posthog = usePostHog();
    const { organization } = useAppContext();
    const { activeSlideId, setActiveSlideId, activeSessionId, isStarted, ...recordingContext } = useLessonPlanRecording();
    const [hasTrackedFirstSlide, setHasTrackedFirstSlide] = useState(false);
    // Track which slides have been tracked by ID to avoid duplicate API calls
    const trackedSlidesRef = useRef<Set<number>>(new Set());
    const hasTrackedOnMountRef = useRef<boolean>(false);

    // Slides are pre-ordered by the backend (slide_number); filter nulls and convert to display format
    const slides = (session.slides || [])
        .filter((slide): slide is SessionSlideModel => slide != null)
        .map(slide => ({
            ...slide,
            scripts: {
                open: slide.script_open,
                on_neutral: slide.script_on_neutral,
                on_negative: slide.script_on_negative,
                on_positive: slide.script_on_positive
            }
        }));
    const totalSlides = slides.length;
    const isActiveSession = session?.id === activeSessionId;
    const activeSlideIndex = isActiveSession
        ? Math.max(0, slides.findIndex((s) => s.id === activeSlideId))
        : 0;
    // Stable id for the active slide (used in effect deps to avoid re-running when `slides` array reference changes)
    const activeSlideIdForDeck = slides[activeSlideIndex]?.id;

    // Track the initial/current slide when session is expanded and not already tracked
    // Don't call onProgressUpdate on initial mount to avoid triggering refetches
    // Coach and parent can send update_slide_progress to the API
    const trackInitialSlide = useCallback(async () => {
        if (!isPublished || !(myRoles?.includes('coach') || myRoles?.includes('parent')) || !lessonPlanId || !session?.id) {
            return;
        }
        const slide = slides[activeSlideIndex];
        if (!slide?.id) {
            return;
        }
        if (trackedSlidesRef.current.has(slide.id)) {
            return;
        }

        try {
            const now = new Date().toISOString();
            const wasFirst = !hasTrackedFirstSlide;
            const sessionPayload: Record<string, unknown> = {
                slides: {
                    [String(slide.id)]: { started_at: now }
                }
            };
            if (wasFirst) {
                sessionPayload.started_at = now;
                setHasTrackedFirstSlide(true);
            }
            const progressUpdate = {
                sessions: {
                    [String(session.id)]: sessionPayload
                }
            };

            await ApiClient.patch(`/api/lesson-plan/${lessonPlanId}/update_slide_progress/`, progressUpdate);
            trackedSlidesRef.current.add(slide.id);
            // Update local state so Mark Complete becomes visible without refetch
            updateSlideProp?.(slide.id, { started_at: now });
            if (wasFirst) updateSessionProp?.(session.id, { started_at: now });
        } catch (error) {
            console.error('Error tracking slide progress:', error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPublished, myRoles, lessonPlanId, session?.id, activeSlideIndex, slides, updateSlideProp, updateSessionProp]);

    useEffect(() => {
        // Only track if expanded and we haven't tracked on mount yet
        if (isExpanded && !hasTrackedOnMountRef.current) {
            hasTrackedOnMountRef.current = true;
            trackInitialSlide();
        }
    }, [isExpanded, trackInitialSlide]);

    // PostHog: fire lesson_slide_viewed when user lands on a slide (all users)
    useEffect(() => {
        const slide = slides[activeSlideIndex];
        if (!posthog || !slide?.id || !lessonPlanId || !session?.id) return;
        const slideNumber = activeSlideIndex + 1;
        capture(posthog, "lesson_slide_viewed", {
            lesson_plan_id: lessonPlanId,
            session_id: session.id,
            session_number: session.session_number ?? session.id,
            slide_id: slide.id,
            slide_number: slideNumber,
            total_slides: totalSlides,
            is_first_slide: slideNumber === 1,
            is_last_slide: slideNumber === totalSlides,
            organization_id: organization?.id
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [posthog, activeSlideIndex, lessonPlanId, session?.id, totalSlides, organization?.id]);

    // Automatic restart: only when user has manually started and this deck is the active session.
    // Recording always requires an initial manual Start; then we upload/restart between slides until Stop.
    // Dependencies use activeSlideIdForDeck (primitive) instead of slides to avoid re-running on every
    // render—slides is recreated each render and would otherwise cause start/stop recording loops.
    useEffect(() => {
        if (!lessonPlanId || !session?.id) return;
        if (!isStarted) return; // never auto-start; only restart after user has clicked Start
        if (!isActiveSession) return; // only the active session's deck

        const startSlideRecording = async () => {
            try {
                await recordingContext.startRecording(
                    lessonPlanId,
                    session.id,
                    activeSlideIdForDeck ?? undefined,
                    false // fromUser: restart on slide change, not manual
                );
            } catch (error: any) {
                console.error('Error starting automatic recording:', error);
            }
        };

        startSlideRecording();

        return () => {
            if (recordingContext.isRecording()) {
                recordingContext.stopRecording(/* fromUser: false */).catch((error: any) => {
                    console.error('Error stopping recording on cleanup:', error);
                });
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lessonPlanId, session?.id, activeSlideIndex, activeSlideIdForDeck, isActiveSession, isStarted]);

    if (totalSlides === 0) {
        return (
            <Box sx={{ mt: 3, p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                    No slides available in this lesson plan.
                </Typography>
            </Box>
        );
    }

    const trackSlideProgress = async (slideIndex: number, skipIfTracked: boolean = true, notifyParent: boolean = true) => {
        if (!isPublished || !(myRoles?.includes('coach') || myRoles?.includes('parent')) || !lessonPlanId || !session?.id) {
            return;
        }
        const slide = slides[slideIndex];
        if (!slide?.id) {
            return;
        }
        if (skipIfTracked && trackedSlidesRef.current.has(slide.id)) {
            return;
        }

        try {
            const now = new Date().toISOString();
            const wasFirst = !hasTrackedFirstSlide;
            const sessionPayload: Record<string, unknown> = {
                slides: {
                    [String(slide.id)]: { started_at: now }
                }
            };
            if (wasFirst) {
                sessionPayload.started_at = now;
                setHasTrackedFirstSlide(true);
            }
            const progressUpdate = {
                sessions: {
                    [String(session.id)]: sessionPayload
                }
            };

            await ApiClient.patch(`/api/lesson-plan/${lessonPlanId}/update_slide_progress/`, progressUpdate);
            trackedSlidesRef.current.add(slide.id);
            // Update local state so Mark Complete becomes visible without refetch
            updateSlideProp?.(slide.id, { started_at: now });
            if (wasFirst) updateSessionProp?.(session.id, { started_at: now });
        } catch (error) {
            console.error('Error tracking slide progress:', error);
        }
    };

    const fireSlideNavigated = (fromStep: number, toStep: number, direction: 'next' | 'previous' | 'dropdown') => {
        if (!posthog || !lessonPlanId || !session?.id) return;
        capture(posthog, "lesson_slide_navigated", {
            lesson_plan_id: lessonPlanId,
            session_id: session.id,
            session_number: session.session_number ?? session.id,
            from_slide_number: fromStep + 1,
            to_slide_number: toStep + 1,
            direction,
            organization_id: organization?.id
        });
    };

    const handleSlideChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const newStep = Number(event.target.value);
        if (newStep === activeSlideIndex) return;
        fireSlideNavigated(activeSlideIndex, newStep, 'dropdown');
        if (recordingContext.isRecording()) {
            await recordingContext.stopRecording();
        }
        setActiveSlideId(slides[newStep]?.id ?? null);
        trackSlideProgress(newStep);
        // New recording will start automatically via useEffect
    };

    const handleNext = async () => {
        const newStep = Math.min(activeSlideIndex + 1, totalSlides - 1);
        if (newStep === activeSlideIndex) return;
        fireSlideNavigated(activeSlideIndex, newStep, 'next');
        if (recordingContext.isRecording()) {
            await recordingContext.stopRecording();
        }
        setActiveSlideId(slides[newStep]?.id ?? null);
        trackSlideProgress(newStep);
        // New recording will start automatically via useEffect
    };

    const handleBack = async () => {
        const newStep = Math.max(activeSlideIndex - 1, 0);
        if (newStep === activeSlideIndex) return;
        fireSlideNavigated(activeSlideIndex, newStep, 'previous');
        if (recordingContext.isRecording()) {
            await recordingContext.stopRecording();
        }
        setActiveSlideId(slides[newStep]?.id ?? null);
        // New recording will start automatically via useEffect
    };

    const currentSlide = slides[activeSlideIndex];

    return (
        <Box data-testid={`lesson-plan-slide-deck--session-${session.session_number ?? session.id}`}>
            {/* Slide Navigation */}
            <Box
                sx={{
                    mb: 0,
                    p: 1,
                    borderRadius: "6px 6px 0 0"
                }}
            >

                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
                    <TrackedTextField
                        select
                        label={`Slide ${activeSlideIndex + 1} of ${totalSlides}`}
                        variant="standard"
                        value={activeSlideIndex}
                        onChange={handleSlideChange}
                        sx={{
                            flexGrow: 1
                        }}
                        data-attr="lesson-plan-slide-deck_slide-select"
                    >
                        {slides.map((slide, index) => (
                            <MenuItem key={index} value={index}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    {slide.started_at && (
                                        <TaskAltIcon color="success" fontSize="small" titleAccess="Slide tracked" />
                                    )}
                                    <Typography component="span">{slide.title || `Slide ${index + 1}`}</Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </TrackedTextField>
                    <ButtonGroup variant="outlined" aria-label="slide navigation">
                        <Button
                            onClick={handleBack}
                            disabled={activeSlideIndex === 0}
                            startIcon={<ArrowBackIcon />}
                            data-attr="lesson-plan-slide-deck_previous"
                        >
                            Previous
                        </Button>
                        <Button
                            onClick={handleNext}
                            disabled={activeSlideIndex === totalSlides - 1}
                            endIcon={<ArrowForwardIcon />}
                            data-attr="lesson-plan-slide-deck_next"
                        >
                            Next
                        </Button>
                    </ButtonGroup>
                    {lessonPlanId && isPublished && (
                            isStarted ? (
                                <Button
                                    onClick={() => recordingContext.stopRecording(true).catch((e) => console.error('Stop session recording:', e))}
                                    variant="outlined"
                                    size="small"
                                    startIcon={<StopIcon />}
                                    color="error"
                                    data-attr="lesson-plan-slide-deck_stop-recording"
                                >
                                    Stop Recording
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => recordingContext.startRecording(lessonPlanId!, activeSessionId ?? undefined, activeSlideId ?? undefined, true).catch((e) => console.error('Start session recording:', e))}
                                    variant="outlined"
                                    size="small"
                                    startIcon={<PlayArrowIcon />}
                                    data-attr="lesson-plan-slide-deck_start-recording"
                                >
                                    Start Recording
                                </Button>
                            )
                        )}

                    {lessonPlanId && isPublished && myRoles?.includes('coach') && (
                        <Button
                            component={RouterLink}
                            to={`/recordings?lesson_plan_id=${lessonPlanId}${session?.id ? `&session_id=${session.id}` : ''}`}
                            variant="outlined"
                            size="small"
                            data-attr="lesson-plan-slide-deck_view-recordings"
                        >
                            View Recordings
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Current Slide */}
            {currentSlide && (
                <LessonPlanSlide
                    slide={currentSlide}
                    slideNumber={activeSlideIndex + 1}
                    totalSlides={totalSlides}
                    myRoles={myRoles}
                />
            )}
        </Box>
    );
};

export default LessonPlanSlideDeck;

